/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
} from '@angular/core';

import { DialogService } from '@ngneat/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import {
  ProductExtra,
  CarserviceEstimate,
  InfoSection,
  MechanicalOrder,
} from 'rest-client';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { BookingData } from '@app/modules/core/states/booking-state/booking.model';
import { CarServicesStateModel } from '@app/modules/core/states/calculated-price-state/calculated-price.model';
import { CalculatedPriceState } from '@app/modules/core/states/calculated-price-state/calculated-price.state';
import { RemoveServiceRequestAction } from '@app/modules/core/states/offer-state/offer.actions';
import { OfferStateModel } from '@app/modules/core/states/offer-state/offer.model';
import { TripStateModel } from '@app/modules/core/states/trip-state/trip.model';
import { DIALOG_CONFIG } from '@app/modules/core/utils/dialog.config';

import {
  CarServiceOperationData,
  CarServiceOperationItem,
  CarServicePartData,
  CarServicePartItem,
  CarServicePriceData,
  InfoSectionDialogData,
} from '../../models';
import {
  getExtraInfoSection,
  getPriceInfoSection,
  getCarInfoSectionFromEstimate,
  buildViewCarServiceOperationsData,
  getOperationsInfoSection,
  getDetailPriceInfoSection,
  getPartsInfoSection,
  buildViewCarServicePartsData,
} from '../../utils/info-sections';
import { CarservicesDiscountDialogComponent } from '../carservices-discount-dialog/carservices-discount-dialog.component';
import { InfoSectionDialogComponent } from '../info-section-dialog/info-section-dialog.component';

const ZERO = 0;
const VAT = 1.21;
const TYPE_WASH = 'wash';
const SERVICE_TYPE_SPACE = '2';
const SOURCE_URBAN = 'urban';

const PROMO_PRODUCT = 1;
const PROMO_EXTRAS = 2;
const PROMO_CARSERVICES = 3;

// eslint-disable-next-line @typescript-eslint/no-unused-vars

@UntilDestroy()
@Component({
  selector: 'bkg-summary-price',
  templateUrl: './summary-price.component.html',
})
export class SummaryPriceComponent implements OnChanges {
  @Input() offerData: OfferStateModel;
  @Input() readOnly: boolean;
  @Input() bookingData: BookingData;
  @Input() sourceMode: string;
  @Input() testABMode: boolean;
  @Input() tripData: TripStateModel;

  @Output() modifyServicesReturn = new EventEmitter<void>();
  @Output() removeExtraReturn = new EventEmitter<ProductExtra>();
  @Output() removeCarServiceReturn = new EventEmitter<CarserviceEstimate>();
  @Output() removePromoReturn = new EventEmitter<void>();
  @Output() removePointsReturn = new EventEmitter<void>();

  @Output() sampleReturn = new EventEmitter<void>();

  @Select(CalculatedPriceState.carServices)
  carServices$: Observable<CarServicesStateModel>;

  selectedPlace: ProductExtra;
  selectedWash: ProductExtra;

  carServices: any;

  constructor(
    private dialog: DialogService,
    private store: Store,
    private translateService: TranslateService
  ) {
    this.subscribeToCarservices();
  }

  private subscribeToCarservices(): void {
    this.carServices$
      .pipe(untilDestroyed(this))
      .subscribe(
        (carServices: CarServicesStateModel) => (this.carServices = carServices)
      );
  }

  get pvpProductRaw(): boolean {
    return +this.offerData.offer?.pvp_product_raw > ZERO;
  }

  get pvpProductOption(): boolean {
    return +this.offerData.offer?.pvp_product_option > ZERO;
  }

  get pvpProductWash(): boolean {
    return +this.offerData.offer?.pvp_wash > ZERO;
  }

  get pvpDoubleSpace(): boolean {
    return +this.offerData.offer?.pvp_double_space > ZERO;
  }
  get hasCustomServices(): boolean {
    return !!this.offerData.service_request;
  }

  get hasCustomTiresService(): boolean {
    return !!this.offerData.customized_tires_request;
  }

  get pvpMechanicalOrders(): boolean {
    return +this.offerData.offer?.pvp_mechanical_orders > ZERO;
  }

  get pvpMechanicalOrderFerry(): boolean {
    return +this.offerData.offer?.pvp_mechanical_order_ferry > ZERO;
  }

  get pvpPoints(): boolean {
    return +this.offerData.offer?.pvp_product_points > ZERO;
  }

  get pvpPromo(): boolean {
    return +this.offerData.offer?.pvp_promotion > ZERO;
  }
  get pvpCarServiceDiscount(): boolean {
    return this.offerData.offer?.pvp_carservice_discount > ZERO;
  }

  get productName(): string {
    return this.bookingData?.product?.name;
  }

  get showSection(): boolean {
    return this.sourceMode === SOURCE_URBAN &&
      !this.pvpMechanicalOrders &&
      !this.offerData?.service_request &&
      !this.offerData?.customized_tires_request &&
      !this.pvpProductWash
      ? false
      : true;
  }

  get taxesSentence(): string {
    let taxes = '';
    if (this.bookingData?.product?.location?.city === 'Barcelona') {
      taxes = this.translateService.instant(
        'sharedModule.bkgSummaryPrice.taxesSentence2'
      ) as string;
    } else {
      taxes = this.translateService.instant(
        'sharedModule.bkgSummaryPrice.taxesSentence3'
      ) as string;
    }
    return taxes;
  }

  get showTaxes(): boolean {
    return this.offerData?.offer?.mechanical_orders?.some(
      // eslint-disable-next-line eqeqeq
      (mo: MechanicalOrder) =>
        mo.mechanical_estimate?.car_service_estimates.some(
          // eslint-disable-next-line eqeqeq
          (estimate: CarserviceEstimate) => this.checkFeesNotIncluded(estimate)
        )
    );
  }

  ngOnChanges(): void {
    this.selectedPlace = this.offerData?.offer?.extras?.find(
      (extra: ProductExtra) => extra.service_type === SERVICE_TYPE_SPACE
    );
    if (this.pvpProductWash) {
      this.selectedWash = this.offerData.offer?.extras.find(
        (extra: ProductExtra) => extra.type === TYPE_WASH
      );
    }
  }

  priceWithVAT(price: number): number {
    return price * VAT;
  }

  onSampleReturn(): void {
    this.sampleReturn.emit();
  }

  onClickDetailExtra(selectedExtra: ProductExtra): void {
    const infoSections: InfoSection[] = [];
    infoSections.push(getExtraInfoSection(selectedExtra));
    const vatIncludedText = this.translateService.instant(
      'common.vatIncluded'
    ) as string;
    infoSections.push(getPriceInfoSection(selectedExtra.price, vatIncludedText));
    this.openExtraDialog(selectedExtra, infoSections);
  }

  onClickDetailCustomService(): void {
    const infoSections: InfoSection[] = [];
    const extraInfo = new InfoSection();
    extraInfo.body = this.offerData.service_request;
    infoSections.push(extraInfo);
    const data: InfoSectionDialogData = {
      title: this.translateService.instant(
        'sharedModule.bkgSummaryPrice.customService'
      ) as string,
      infoSections,
      imageGallery: null,
      confirmTextTranslationKey:
        'sharedModule.bkgInfoSectionDialog.removeFromMyBooking',
    };

    this.dialog
      .open(InfoSectionDialogComponent, {
        ...DIALOG_CONFIG,
        width: '700px',
        data,
      })
      .afterClosed$.pipe(filter(Boolean), untilDestroyed(this))
      .subscribe(() => this.onClickRemoveCustomService());
  }

  onClickDiscountCarService(): void {
    const total = this.offerData.offer?.pvp_mechanical_orders
      ? this.offerData.offer?.pvp_mechanical_orders
      : 0;
    const discount =
      this.offerData.offer?.pvp_mechanical_orders > 99
        ? 25
        : this.offerData.offer?.pvp_mechanical_orders > 199
          ? 50
          : this.offerData.offer?.pvp_mechanical_orders > 299
            ? 75
            : this.offerData.offer?.pvp_mechanical_orders > 499
              ? 75
              : 0;
    this.dialog.open(CarservicesDiscountDialogComponent, {
      ...DIALOG_CONFIG,
      size: 'md',
      enableClose: false,
      data: {
        title: this.translateService.instant(
          'sharedModule.bkgSummaryPrice.carDiscountTitle'
        ) as string,
        body: this.translateService.instant(
          'sharedModule.bkgSummaryPrice.carDiscountBody',
          { total: total, discount: discount }
        ) as string,
      },
    });
  }

  onClickFerryCarService(): void {
    this.dialog.open(CarservicesDiscountDialogComponent, {
      ...DIALOG_CONFIG,
      size: 'md',
      enableClose: false,
      data: {
        title: this.translateService.instant(
          'sharedModule.bkgSummaryPrice.removal'
        ) as string,
        body: this.translateService.instant(
          'sharedModule.bkgSummaryPrice.carFerryBody'
        ) as string,
      },
    });
  }

  onClickRemoveExtra(selectedExtra: ProductExtra): void {
    this.removeExtraReturn.emit(selectedExtra);
  }

  onClickRemoveCustomService(): void {
    this.store.dispatch(new RemoveServiceRequestAction());
  }

  onClickDetailCarService(estimate: CarserviceEstimate): void {
    const infoSections: InfoSection[] = [];
    const carServicePrice = {} as CarServicePriceData;
    let carServicesParts: CarServicePartData = null;
    let carServiceOperations: CarServiceOperationData = null;
    infoSections.push(getCarInfoSectionFromEstimate(estimate));

    const parts = this.checkPartsUniquePrice(estimate);
    const operations = this.checkOperationsUniquePrice(estimate);

    if (!!parts) {
      carServicesParts = buildViewCarServicePartsData(parts);
      infoSections.push(getPartsInfoSection());
    }

    if (!!operations) {
      carServiceOperations = buildViewCarServiceOperationsData(operations);
      infoSections.push(getOperationsInfoSection());
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { base_standard_total } = estimate.mechanical_estimate_data;

    carServicePrice.base = +base_standard_total;
    carServicePrice.vat = this.getPriceVat(+base_standard_total);
    carServicePrice.price = this.getPriceWithVAT(+base_standard_total);
    infoSections.push(getDetailPriceInfoSection());

    this.openCarServiceDialog(
      estimate,
      infoSections,
      carServicesParts,
      carServiceOperations,
      carServicePrice
    );
  }

  checkOperationsUniquePrice(
    estimate: CarserviceEstimate
  ): CarServiceOperationItem[] {
    const { mechanical_estimate_data: estimateData } = estimate;

    if (estimateData.unique_price) {
      const reasignedUniquePriceOperations: any[] = [];

      estimateData.operations.forEach((operation: any) => {
        reasignedUniquePriceOperations.push(Object.assign({}, operation));
      });

      reasignedUniquePriceOperations.forEach((operation: any) => {
        operation.base_standard = null;
      });

      return reasignedUniquePriceOperations as CarServiceOperationItem[];
    } else {
      return estimateData.operations as CarServiceOperationItem[];
    }
  }

  checkPartsUniquePrice(estimate: CarserviceEstimate): CarServicePartItem[] {
    const { mechanical_estimate_data: estimateData } = estimate;
    if (estimateData.unique_price) {
      const reasignedUniquePriceParts: any[] = [];

      estimateData.parts.forEach((part: any) => {
        reasignedUniquePriceParts.push(Object.assign({}, part));
      });

      reasignedUniquePriceParts.forEach((part: any) => {
        part.base_standard = null;
      });

      return reasignedUniquePriceParts as CarServicePartItem[];
    } else {
      return estimateData.parts as CarServicePartItem[];
    }
  }

  getPriceVat(base: number): number {
    return this.getPriceWithVAT(base) - base;
  }

  getPriceWithVAT(base: number): number {
    return base * VAT;
  }

  onClickRemoveCarService(estimate: CarserviceEstimate): void {
    this.removeCarServiceReturn.emit(estimate);
  }

  checkFeesNotIncluded(estimate: CarserviceEstimate): boolean {
    if (this.carServices) {
      for (let i = 0; i < this.carServices?.length; i++) {
        for (let j = 0; j < this.carServices?.[i]?.car_services?.length; j++) {
          if (
            +this.carServices[i].car_services[j].fees_not_included &&
            +this.carServices[i].car_services[j].id === +estimate.id_car_service
          ) {
            return true;
          }
        }
      }
    }

    return false;
  }

  onClickRemovePromo(): void {
    this.removePromoReturn.emit();
  }

  onClickRemovePoints(): void {
    this.removePointsReturn.emit();
  }

  private openExtraDialog(
    selectedExtra: ProductExtra,
    infoSections: InfoSection[]
  ): void {
    const data: InfoSectionDialogData = {
      title: selectedExtra?.name,
      infoSections,
      imageGallery: selectedExtra.image_gallery,
      confirmTextTranslationKey:
        'sharedModule.bkgInfoSectionDialog.removeFromMyBooking',
    };

    this.dialog
      .open(InfoSectionDialogComponent, {
        ...DIALOG_CONFIG,
        width: '700px',
        data,
      })
      .afterClosed$.pipe(filter(Boolean), untilDestroyed(this))
      .subscribe(() => this.onClickRemoveExtra(selectedExtra));
  }

  private openCarServiceDialog(
    estimate: CarserviceEstimate,
    infoSections: InfoSection[],
    carServiceParts: CarServicePartData,
    carServiceOperations: CarServiceOperationData,
    carServicePrice: CarServicePriceData
  ): void {
    const data: InfoSectionDialogData = {
      title: estimate?.name,
      infoSections,
      estimateParts: carServiceParts,
      estimateOperations: carServiceOperations,
      carServicePrice: carServicePrice,
      confirmTextTranslationKey:
        'sharedModule.bkgInfoSectionDialog.removeFromMyBooking',
    };

    this.dialog
      .open(InfoSectionDialogComponent, {
        ...DIALOG_CONFIG,
        width: '700px',
        data,
      })
      .afterClosed$.pipe(filter(Boolean), untilDestroyed(this))
      .subscribe(() => this.onClickRemoveCarService(estimate));
  }
}
